﻿import {hasFlag, translation} from "@2d-front-monorepo/core";
import {FrontendEnabledMenus} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useEffect, useRef} from "react";
import {NavLink, Outlet, useLocation} from "react-router";
import MaterialIcon from "../components/MaterialIcon";
import {HttpContext} from "../contexts/HttpContext";
import {PermitsProvider} from "../contexts/PermitsContext";
import {getGlobalContext} from "../hooks/getGlobalContext";
import {useHolderNotifications} from "../hooks/useHolderNotifications";
import {useLogout} from "../hooks/useLogout";
import {PrivateRoutes} from "../routes";

function updateNavigationType(navRef: RefObject<HTMLElement | null>): void {
	const nav = navRef.current;
	if (nav == null) {
		return;
	}

	if (window.innerWidth <= 640) {
		nav.setAttribute("popover", "auto");

	} else {
		nav.removeAttribute("popover");
	}
}

function closePopover(navRef: RefObject<HTMLElement | null>): void {
	const nav = navRef.current;
	if (nav == null) {
		return;
	}

	if (nav.hasAttribute("popover")) {
		nav.hidePopover();
	}
}

export function MenuLayout(): JSX.Element {
	const location = useLocation();
	const navRef = useRef<HTMLElement>(null);
	const {installation} = getGlobalContext();
	const logout = useLogout();
	const enabledMenus = installation.configuration.ui.enabledMenus;

	useEffect(() => {
		HttpContext.reset();
	}, [location.pathname]);

	useEffect(() => {
		const ro = new ResizeObserver(() => updateNavigationType(navRef));
		ro.observe(document.body);
		return () => ro.disconnect();
	}, []);

	useHolderNotifications();

	return (
		<div className="menu-layout">
			<nav id="navigation-menu" ref={navRef} onClick={() => closePopover(navRef)} onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") closePopover(navRef);
			}}>
				<ul>
					{hasFlag(enabledMenus, FrontendEnabledMenus.NewPermits) && <li>
                        <NavLink to={PrivateRoutes.permitsHome}>
                            <MaterialIcon icon="add_circle"/>
                            <span>{translation(PermitTranslations.menu__new)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Permits) && <li>
                        <NavLink to={PrivateRoutes.permitsList}>
                            <MaterialIcon icon="emoji_transportation"/>
                            <span>{translation(PermitTranslations.menu__my_parking)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Payments) && <li>
                        <NavLink to={PrivateRoutes.payments}>
                            <MaterialIcon icon="payments"/>
                            <span>{translation(PermitTranslations.menu__payments)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Documents) && <li>
                        <NavLink to={PrivateRoutes.documents}>
                            <MaterialIcon icon="docs"/>
                            <span>{translation(PermitTranslations.menu__documents)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Vehicles) && <li>
                        <NavLink to={PrivateRoutes.vehicles}>
                            <MaterialIcon icon="directions_car"/>
                            <span>{translation(PermitTranslations.menu__vehicles)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Invoices) && <li>
                        <NavLink to={PrivateRoutes.invoices}>
                            <MaterialIcon icon="newsmode"/>
                            <span>{translation(PermitTranslations.menu__invoices)}</span>
                        </NavLink>
                    </li>}
					{hasFlag(enabledMenus, FrontendEnabledMenus.Settings) && <li>
                        <NavLink to={PrivateRoutes.settings}>
                            <MaterialIcon icon="account_circle"/>
                            <span>{translation(PermitTranslations.menu__settings)}</span>
                        </NavLink>
                    </li>}
					<li id="logout-button__mobile">
						<button type="button" onClick={logout}>
							<MaterialIcon icon="logout"/>
							{translation(PermitTranslations.log_out)}
						</button>
					</li>
				</ul>
			</nav>
			<section className="main-content">
				<PermitsProvider><Outlet/></PermitsProvider>
			</section>
		</div>
	);
}
